import { useEffect, useState, useMemo } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
  Spinner,
  Card,
  CardBody,
  Checkbox,
} from '@chakra-ui/react'
import {
  createRatePerClientSchema,
  updateRatePerClientSchema,
  type CreateRatePerClientFormData,
  type UpdateRatePerClientFormData,
  ratePerClientSizes,
} from '@/lib/validations/auth'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'
import { SearchableSelect } from '@/components/common/SearchableSelect'

interface RatePerClientFormProps {
  ratePerClientId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type RatePerClientFormValues = CreateRatePerClientFormData | UpdateRatePerClientFormData

export function RatePerClientForm({ ratePerClientId, onSuccess, onCancel }: RatePerClientFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const colors = useThemeColors()
  const isEditMode = !!ratePerClientId
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [shippingLines, setShippingLines] = useState<Array<{ id: number; name: string }>>([])
  const [cypas, setCypas] = useState<Array<{ id: number; name: string }>>([])

  const cypasWithAll = useMemo(() => {
    const allOption = { id: 0, name: 'All CY' }
    return [allOption, ...cypas]
  }, [cypas])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<RatePerClientFormValues>({
    resolver: zodResolver(isEditMode ? updateRatePerClientSchema : createRatePerClientSchema),
    defaultValues: {
      shipping_line_id: undefined as any,
      no_of_days: undefined as any,
      requirements: '',
      remarks: '',
      cypa_id: undefined as any,
      stack_run: undefined as any,
      container_size: undefined as any,
      rate: undefined as any,
      has_vat: false,
      is_active: 1,
    },
  })

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      try {
        let record: Record<string, unknown> = {}
        if (isEditMode && ratePerClientId) {
          const { data } = await apiClient.get(`/rate-per-clients/${ratePerClientId}`)
          record = (data?.data || data || {}) as Record<string, unknown>
        }

        const [shippingLineRes, cypaRes] = await Promise.all([
          apiClient.get('/shipping-lines'),
          apiClient.get('/container-yards/yard-list', { params: { is_active: 1 } }),
        ])

        let shippingLineData = (shippingLineRes.data?.data || shippingLineRes.data || []) as Array<{ id: number; name: string }>
        let cypaData = (cypaRes.data?.data || cypaRes.data || []) as Array<{ id: number; name: string }>

        if (isEditMode && Object.keys(record).length > 0) {
          const shippingLineId = record.shipping_line_id as number | undefined
          const shippingLine = record.shipping_line as { id?: number; name?: string } | undefined
          if (shippingLineId != null && !shippingLineData.some((s) => s.id === shippingLineId)) {
            shippingLineData = [
              { id: shippingLineId, name: shippingLine?.name ?? 'Unknown' },
              ...shippingLineData,
            ]
          }
          const cypaId = record.cypa_id as number | undefined
          const cypa = record.cypa as { id?: number; name?: string } | undefined
          // Skip injecting when cypa_id is 0 (All CY) since it's always in the list via cypasWithAll
          if (cypaId != null && cypaId !== 0 && !cypaData.some((c) => c.id === cypaId)) {
            cypaData = [{ id: cypaId, name: cypa?.name ?? 'Unknown' }, ...cypaData]
          }
        }

        setShippingLines(shippingLineData)
        setCypas(cypaData)

        if (isEditMode && ratePerClientId && Object.keys(record).length > 0) {
          reset({
            shipping_line_id: (record.shipping_line_id ?? (record.shipping_line as { id?: number })?.id) as number | undefined,
            no_of_days: (record.no_of_days ?? undefined) as number | undefined,
            requirements: (record.requirements as string) ?? '',
            remarks: (record.remarks as string) ?? '',
            cypa_id: (record.cypa_id ?? (record.cypa as { id?: number })?.id) as number | undefined,
            stack_run: (record.stack_run ?? undefined) as number | undefined,
            container_size: record.container_size as RatePerClientFormValues['container_size'] | undefined,
            rate: (record.rate ?? undefined) as number | undefined,
            has_vat: record.has_vat !== undefined ? (record.has_vat as boolean) : false,
            is_active: (record.is_active ?? 1) as number,
          })
        } else {
          reset((prev) => ({ ...prev, is_active: 1 }))
        }
      } catch (error: any) {
        showError(error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [isEditMode, ratePerClientId, reset, showError])

  const onSubmit = async (data: RatePerClientFormValues) => {
    setIsSubmitting(true)
    try {
      const payload = {
        shipping_line_id: data.shipping_line_id,
        no_of_days: data.no_of_days,
        requirements: data.requirements,
        remarks: data.remarks || '',
        cypa_id: data.cypa_id,
        stack_run: data.stack_run,
        container_size: data.container_size,
        rate: data.rate,
        has_vat: data.has_vat ?? false,
        is_active: data.is_active ?? 1,
      }

      if (isEditMode && ratePerClientId) {
        await apiClient.put(`/rate-per-clients/${ratePerClientId}`, payload)
        showSuccess('Success', 'Rate per client updated successfully')
      } else {
        await apiClient.post('/rate-per-clients', payload)
        showSuccess('Success', 'Rate per client created successfully')
      }

      onSuccess?.()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <VStack spacing={4} align="center" py={6}>
            <Spinner color="blue.500" />
            <Text color={colors.text}>Loading form...</Text>
          </VStack>
        </CardBody>
      </Card>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Shipping Line <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.shipping_line_id}>
            <Controller
              name="shipping_line_id"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  options={shippingLines}
                  optionsUrl="/shipping-lines"
                  value={field.value ?? null}
                  onChange={(value) => field.onChange(value !== null ? Number(value) : undefined)}
                  placeholder="Select shipping line"
                  isInvalid={!!errors.shipping_line_id}
                />
              )}
            />
            <FormErrorMessage>{errors.shipping_line_id?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Container Yard <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.cypa_id}>
            <Controller
              name="cypa_id"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  options={cypasWithAll}
                  optionsUrl="/container-yards/yard-list"
                  optionsParams={{ is_active: 1 }}
                  value={field.value ?? null}
                  onChange={(value) => field.onChange(value !== null ? Number(value) : undefined)}
                  placeholder="Select container yard"
                  isInvalid={!!errors.cypa_id}
                />
              )}
            />
            <FormErrorMessage>{errors.cypa_id?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            No. of Days <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.no_of_days}>
            <Input type="number" {...register('no_of_days', { valueAsNumber: true })} placeholder="e.g. 5" min={1} />
            <FormErrorMessage>{errors.no_of_days?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Requirements
          </FormLabel>
          <FormControl isInvalid={!!errors.requirements}>
            <Textarea {...register('requirements')} placeholder="Enter requirements (optional)" rows={3} />
            <FormErrorMessage>{errors.requirements?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Remarks
          </FormLabel>
          <FormControl isInvalid={!!errors.remarks}>
            <Textarea {...register('remarks')} placeholder="Additional remarks (optional)" rows={3} />
            <FormErrorMessage>{errors.remarks?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Stack Run <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.stack_run}>
            <Input
              type="number"
              step="0.01"
              min={0}
              {...register('stack_run', { valueAsNumber: true })}
              placeholder="Enter stack run amount"
            />
            <FormErrorMessage>{errors.stack_run?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Container Size <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.container_size}>
            <Select placeholder="Select container size" {...register('container_size')}>
              {ratePerClientSizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.container_size?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Rate <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.rate}>
            <Input type="number" step="0.01" min={0} {...register('rate', { valueAsNumber: true })} placeholder="Enter rate" />
            <FormErrorMessage>{errors.rate?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Has VAT?
          </FormLabel>
          <FormControl isInvalid={!!errors.has_vat}>
            <Controller
              name="has_vat"
              control={control}
              render={({ field }) => (
                <Checkbox
                  isChecked={field.value ?? false}
                  onChange={(e) => field.onChange(e.target.checked)}
                >
                  Has VAT
                </Checkbox>
              )}
            />
            <FormErrorMessage>{errors.has_vat?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Saving...' : 'Creating...'}>
            {isEditMode ? 'Save' : 'Create Rate per Client'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}

